// Onde fica o pin de um tópico BCF cujo viewpoint não traz posição.
//
// Os viewpoints capturados na plataforma guardam `anchor` (o centro dos
// elementos seleccionados, ou o alvo da câmara: viewpointCapture.ts) e os
// dos conflitos guardam o ponto da interferência. Os que vêm de um
// .bcfzip importado não trazem nenhum: o BCF não tem esse campo. Para
// esses calcula-se aqui, a pedido, sem tocar no ficheiro:
//
//   1. o centro da caixa dos elementos seleccionados do viewpoint
//      (Components > Selection, por IfcGuid), resolvidos nos modelos
//      abertos;
//   2. sem elementos resolvidos, um ponto à frente da câmara do viewpoint:
//      o alvo, quando o há; senão, ao longo da direcção (ver
//      `pontoAFrenteDaCamara`).
//
// Coordenadas do mundo three (Y para cima), as mesmas de `anchor`. A
// parte que precisa do viewer está em pinAncoraCena.ts.

import type { Viewpoint, VpCamera, VpVec3 } from '@viewer/lib/viewpoint'

export type Ponto = [number, number, number]

/** Distância à frente da câmara, em metros, quando não há alvo nem
 *  modelo à frente: o suficiente para o pin ficar no campo de visão de
 *  quem abriu o viewpoint, sem o pôr colado à lente. */
export const DISTANCIA_A_FRENTE_M = 10

export interface CaixaSimples {
  min: VpVec3
  max: VpVec3
}

/** Centro de uma caixa. */
export function centroDaCaixa(c: CaixaSimples): Ponto {
  return [(c.min.x + c.max.x) / 2, (c.min.y + c.max.y) / 2, (c.min.z + c.max.z) / 2]
}

/**
 * Onde o raio `origem + t * dir` (t >= 0) está dentro da caixa: [t0, t1],
 * com t0 < 0 quando a origem está dentro. Null quando não a atravessa.
 * Método das placas, eixo a eixo.
 */
export function intervaloNaCaixa(origem: VpVec3, dir: VpVec3, c: CaixaSimples): [number, number] | null {
  let t0 = Number.NEGATIVE_INFINITY
  let t1 = Number.POSITIVE_INFINITY
  for (const eixo of ['x', 'y', 'z'] as const) {
    const o = origem[eixo]
    const d = dir[eixo]
    const lo = c.min[eixo]
    const hi = c.max[eixo]
    if (Math.abs(d) < 1e-12) {
      if (o < lo || o > hi) return null
      continue
    }
    let a = (lo - o) / d
    let b = (hi - o) / d
    if (a > b) [a, b] = [b, a]
    t0 = Math.max(t0, a)
    t1 = Math.min(t1, b)
    if (t0 > t1) return null
  }
  if (t1 < 0) return null
  return [t0, t1]
}

function normalizada(v: VpVec3): VpVec3 | null {
  const n = Math.hypot(v.x, v.y, v.z)
  if (!Number.isFinite(n) || n < 1e-12) return null
  return { x: v.x / n, y: v.y / n, z: v.z / n }
}

/**
 * Um ponto à frente da câmara.
 *
 * - Com alvo (`target`), o alvo.
 * - Com a caixa dos modelos e o olhar a atravessá-la: de fora, o ponto
 *   onde o olhar entra no modelo mais metade do percurso lá dentro, até
 *   `DISTANCIA_A_FRENTE_M`; de dentro, `DISTANCIA_A_FRENTE_M` ou a saída
 *   da caixa, o que vier primeiro. O pin fica sobre o que o viewpoint
 *   mostra e não no vazio à frente de uma fachada.
 * - Sem caixa, ou a olhar para fora dela: `DISTANCIA_A_FRENTE_M` à frente.
 *
 * Null com uma direcção nula ou valores não finitos.
 */
export function pontoAFrenteDaCamara(
  camera: Pick<VpCamera, 'position' | 'direction' | 'target'>,
  caixaDosModelos: CaixaSimples | null = null,
): Ponto | null {
  const p = camera.position
  if (![p.x, p.y, p.z].every(Number.isFinite)) return null
  if (camera.target && [camera.target.x, camera.target.y, camera.target.z].every(Number.isFinite)) {
    return [camera.target.x, camera.target.y, camera.target.z]
  }
  const d = normalizada(camera.direction)
  if (!d) return null
  let t = DISTANCIA_A_FRENTE_M
  const intervalo = caixaDosModelos ? intervaloNaCaixa(p, d, caixaDosModelos) : null
  if (intervalo) {
    const [t0, t1] = intervalo
    t = t0 > 0 ? t0 + Math.min(DISTANCIA_A_FRENTE_M, (t1 - t0) / 2) : Math.min(DISTANCIA_A_FRENTE_M, t1)
  }
  return [p.x + d.x * t, p.y + d.y * t, p.z + d.z * t]
}

/**
 * A câmara do viewpoint que serve para o pin, já no mundo three, ou null.
 *
 * - Imagem anexada (`so_imagem`): não tem vista.
 * - Importado com o `.bcfv` original: a câmara relida do ficheiro com o
 *   referencial dos modelos abertos agora (`lerOriginal`), a mesma
 *   conversão da importação (bcfVisinfo.ts com bcfReferencial.ts). Um
 *   viewpoint importado antes de abrir o modelo ficou com a identidade;
 *   reler acerta-o.
 * - Importado sem o original (`components_only` antigo): a câmara
 *   guardada é a neutra, na origem, e não diz nada.
 * - Os outros (capturados, conflitos): a câmara guardada, que já está no
 *   mundo.
 */
export function camaraDoViewpoint(
  vp: Pick<Viewpoint, 'camera' | 'components_only' | 'bcfv_original' | 'so_imagem'>,
  lerOriginal: (xml: string) => VpCamera | null,
): VpCamera | null {
  if (vp.so_imagem) return null
  if (vp.bcfv_original?.xml) return lerOriginal(vp.bcfv_original.xml)
  if (vp.components_only) return null
  return vp.camera ?? null
}

/** Chave da cache de uma âncora calculada: o tópico, o viewpoint e os
 *  modelos abertos (outros modelos, outra caixa). */
export function chaveDaAncora(
  topicGuid: string,
  viewpointId: string,
  modelos: readonly { id: string; sha256?: string | null }[],
): string {
  return `${topicGuid}|${viewpointId}|${modelos.map((m) => `${m.id}:${m.sha256 ?? ''}`).join(',')}`
}

/** Quanto tempo o conjunto de modelos abertos tem de ficar parado (sem
 *  aberturas) antes de se calcularem âncoras: a recuperação da sessão
 *  reabre os IFC um a um, e cada um muda o conjunto. */
export const ESPERA_DE_ESTABILIDADE_MS = 1000

/**
 * Pode-se calcular âncoras agora? Só com os pins ligados e visíveis, o
 * viewer de pé, a recuperação da sessão decidida e os modelos estáveis:
 * nenhum a abrir e o conjunto parado há `ESPERA_DE_ESTABILIDADE_MS`.
 *
 * Calcular durante as aberturas punha dezenas de leituras na fila do
 * worker do Fragments enquanto ele monta os tiles e os materiais do
 * modelo novo; na recuperação do Cha139 (5 IFC, um BCF de 66 tópicos)
 * a abertura ficava presa com o erro do MaterialManager.processHighlight
 * ("materialDefinitions[highlightIndex] is undefined").
 */
export function podeCalcularAncoras(o: {
  ligado: boolean
  visivel: boolean
  mundoPronto: boolean
  recuperacaoDecidida: boolean
  aCarregar: boolean
  modelosEstaveis: boolean
}): boolean {
  return o.ligado && o.visivel && o.mundoPronto && o.recuperacaoDecidida && !o.aCarregar && o.modelosEstaveis
}
